import { COUNTERPARTY_RATING_BG_BY_LABEL, CounterpartyRatingProps } from '../../utils/rating';
import { NineBoxCellProps, NineBoxCellToneProps, NineBoxMetricDataProps, NineBoxRatingProps, NineBoxYearProps } from './mockData';

type NineBoxPaletteModeProps = 'dark' | 'light';

const DARK_TONE_STYLE_BY_CELL: Record<NineBoxCellToneProps, { bg: string; color: string }> = {
  'green-1': { bg: '#67b287', color: '#123424' },
  'green-2': { bg: '#3e986b', color: '#f4fff8' },
  'green-3': { bg: '#226f4b', color: '#f4fff8' },
  'yellow-1': { bg: '#c6a654', color: '#342606' },
  'yellow-2': { bg: '#a57f26', color: '#fff6dc' },
  'yellow-3': { bg: '#7b5910', color: '#fff6dc' },
  'red-1': { bg: '#c27a8b', color: '#4f1525' },
  'red-2': { bg: '#9d4d60', color: '#fff1f4' },
  'red-3': { bg: '#6d2739', color: '#fff1f4' },
  'positive-1': { bg: '#174c37', color: '#f4fff8' },
  'positive-2': { bg: '#1d5d44', color: '#f4fff8' },
  'positive-3': { bg: '#236e50', color: '#f4fff8' },
  'positive-4': { bg: '#2b815d', color: '#f4fff8' },
  'positive-5': { bg: '#34956a', color: '#f4fff8' },
  'positive-6': { bg: '#42aa78', color: '#f4fff8' },
  'positive-7': { bg: '#58bf88', color: '#123424' },
  'negative-1': { bg: '#4a2030', color: '#fff1f4' },
  'negative-2': { bg: '#5a2840', color: '#fff1f4' },
  'negative-3': { bg: '#6b3150', color: '#fff1f4' },
  'negative-4': { bg: '#7d3a5f', color: '#fff1f4' },
  'negative-5': { bg: '#8f456f', color: '#fff1f4' },
  'negative-6': { bg: '#a35381', color: '#fff1f4' },
  'negative-7': { bg: '#b96693', color: '#fff1f4' },
  zero: { bg: '#1f344d', color: '#99aec7' },
  red: { bg: '#9d4d60', color: '#fff1f4' },
  green: { bg: '#3e986b', color: '#f4fff8' },
  yellow: { bg: '#a57f26', color: '#fff6dc' },
  orange: { bg: '#a65c15', color: '#fff6ea' },
  neutral: { bg: '#1c437d', color: '#f3f7ff' },
};

const LIGHT_TONE_STYLE_BY_CELL: Record<NineBoxCellToneProps, { bg: string; color: string }> = {
  'green-1': { bg: '#bbe6c6', color: '#1d4f32' },
  'green-2': { bg: '#77cf94', color: 'white' },
  'green-3': { bg: '#3fab6d', color: 'white' },
  'yellow-1': { bg: '#f1e0a8', color: '#6f5110' },
  'yellow-2': { bg: '#e3c059', color: '#5c4108' },
  'yellow-3': { bg: '#d39e2f', color: '#4b3304' },
  'red-1': { bg: '#efc0ca', color: '#7c3244' },
  'red-2': { bg: '#df7f95', color: 'white' },
  'red-3': { bg: '#cb5670', color: 'white' },
  'positive-1': { bg: '#daf2e0', color: '#1e5133' },
  'positive-2': { bg: '#c0ebca', color: '#1d4e32' },
  'positive-3': { bg: '#a0e2b1', color: '#1b4a2f' },
  'positive-4': { bg: '#7fd796', color: '#173d26' },
  'positive-5': { bg: '#5ecc7b', color: 'white' },
  'positive-6': { bg: '#3fc06a', color: 'white' },
  'positive-7': { bg: '#2ea85c', color: 'white' },
  'negative-1': { bg: '#f6dde2', color: '#833646' },
  'negative-2': { bg: '#efc4cd', color: '#7e3142' },
  'negative-3': { bg: '#e7a7b4', color: '#772c3c' },
  'negative-4': { bg: '#dd899a', color: '#6d2434' },
  'negative-5': { bg: '#d16b80', color: 'white' },
  'negative-6': { bg: '#c1546d', color: 'white' },
  'negative-7': { bg: '#aa3e59', color: 'white' },
  zero: { bg: '#dde6f0', color: '#5f7189' },
  red: { bg: '#df7f95', color: 'white' },
  green: { bg: '#77cf94', color: 'white' },
  yellow: { bg: '#e3c059', color: '#5c4108' },
  orange: { bg: '#df7a28', color: 'white' },
  neutral: { bg: '#24518c', color: 'white' },
};

export const OUTER_CARD_RADIUS = '2xl';
export const TABLE_RADIUS = 'xl';
export const TABLE_BORDER_COLOR = 'gray.100';
export const RATING_COLUMN_WIDTH_REM = 5.75;
export const VALUE_COLUMN_MIN_WIDTH_REM = 6.75;
export const HEADER_HEIGHT = '3.6rem';
export const CELL_HEIGHT = '3.4rem';
const ACTIVE_CELL_OVERLAY = 'inset 0 0 0 9999px rgba(0, 0, 0, 0.18)';
const ACTIVE_CELL_RING = 'inset 0 0 0 2px rgba(255, 255, 255, 0.3)';

export const ACTIVE_CELL_STYLE = `${ACTIVE_CELL_OVERLAY}, ${ACTIVE_CELL_RING}`;
export const INACTIVE_CELL_OPACITY = 0.46;

export const EMPTY_CELL: NineBoxCellProps = { label: '-', tone: 'zero' };
export const EMPTY_TOTAL_CELL: NineBoxCellProps = { label: '-', tone: 'neutral' };

export const createEmptyMetricData = (years: NineBoxYearProps[]): NineBoxMetricDataProps =>
  ({
    'A+': Object.fromEntries(years.map((year) => [year, EMPTY_CELL])),
    A: Object.fromEntries(years.map((year) => [year, EMPTY_CELL])),
    B: Object.fromEntries(years.map((year) => [year, EMPTY_CELL])),
    C: Object.fromEntries(years.map((year) => [year, EMPTY_CELL])),
    'S/D': Object.fromEntries(years.map((year) => [year, EMPTY_CELL])),
    TOTAL: Object.fromEntries(years.map((year) => [year, EMPTY_TOTAL_CELL])),
  } as NineBoxMetricDataProps);

export const buildGridTemplateColumns = (yearCount: number) =>
  `${RATING_COLUMN_WIDTH_REM}rem repeat(${yearCount}, minmax(${VALUE_COLUMN_MIN_WIDTH_REM}rem, 1fr))`;

export const buildTableMinWidth = (yearCount: number) => `${RATING_COLUMN_WIDTH_REM + yearCount * VALUE_COLUMN_MIN_WIDTH_REM}rem`;

export const resolveRatingBadgeBackground = (rating: NineBoxRatingProps) =>
  COUNTERPARTY_RATING_BG_BY_LABEL[rating as CounterpartyRatingProps];

export function resolveToneStyleByCell(mode: NineBoxPaletteModeProps) {
  return mode === 'light' ? LIGHT_TONE_STYLE_BY_CELL : DARK_TONE_STYLE_BY_CELL;
}

export const resolveTableSurfaceBackground = (mode: NineBoxPaletteModeProps) => (mode === 'light' ? '#f3f3f3' : 'gray.0');

export const resolveTableHeaderBackground = (mode: NineBoxPaletteModeProps) => (mode === 'light' ? '#e8e8e8' : 'gray.0');

export function resolveTableRatingColumnBackground(mode: NineBoxPaletteModeProps) {
  return mode === 'light' ? '#e8e8e8' : 'transparent';
}

export const resolveTableDividerColor = (mode: NineBoxPaletteModeProps) => (mode === 'light' ? '#d8d8d8' : 'gray.100');

export const resolveTableStructureTextColor = (mode: NineBoxPaletteModeProps) => (mode === 'light' ? '#6f6f6f' : 'gray.500');

export const resolveTotalLabelSurfaceBackground = (mode: NineBoxPaletteModeProps) => (mode === 'light' ? '#e8e8e8' : 'gray.0');

export const resolveTotalLabelTextColor = (mode: NineBoxPaletteModeProps) => (mode === 'light' ? '#2d2d2d' : 'white');
